import { useState, useMemo } from "react";
import axios from "axios";
import { Loader2, Save, Send } from "lucide-react";
import { usePortalAuth } from "../../lib/portalAuth";
import { OW_API, RUBRIC, CATEGORY_TITLE, formatApiErrorDetail } from "../../lib/operatorWalk";

const NOW = new Date();
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DEFAULT_WALKERS = ["Caleb R. Holder, DBA", "Jake Lair"];
const CUSTOM_OPTION = "__custom__";

function emptyScores() {
  const s = {};
  RUBRIC.forEach((c) => c.points.forEach((p) => (s[p.id] = { score: 0, note: "" })));
  return s;
}

function ScoreRow({ point, value, onScore, onNote }) {
  return (
    <div className="py-3 border-b border-brand-100 last:border-0" data-testid={`score-row-${point.id}`}>
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-ink-soft leading-snug">
          <span className="text-brand-400 font-medium">{point.id}.</span> {point.label}
        </p>
        <div className="flex gap-1 shrink-0">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => onScore(point.id, n)}
              className={`w-8 h-8 text-sm font-medium border transition-colors ${
                value.score === n
                  ? "bg-brand-700 text-white border-brand-700"
                  : "bg-white text-brand-700 border-brand-200 hover:border-brand-500"
              }`}
              data-testid={`score-${point.id}-${n}`}
            >
              {n}
            </button>
          ))}
        </div>
      </div>
      <input
        type="text"
        placeholder="Observation note (optional)"
        value={value.note}
        onChange={(e) => onNote(point.id, e.target.value)}
        className="mt-2 w-full border border-brand-100 px-3 py-2 text-sm focus:outline-none focus:border-brand-400"
        data-testid={`note-${point.id}`}
      />
    </div>
  );
}

function scoresFromExisting(existing) {
  const s = emptyScores();
  (existing?.scores || []).forEach((sc) => {
    s[sc.point_id] = { score: sc.score, note: sc.note || "" };
  });
  return s;
}

function oppsFromExisting(existing) {
  const blank = { title: "", category: "", description: "", action: "" };
  const list = (existing?.opportunities || []).map((o) => ({
    title: o.title || "",
    category: o.category || "",
    description: o.description || "",
    action: o.action || "",
  }));
  while (list.length < 3) list.push({ ...blank });
  return list.slice(0, 3);
}

export default function ReportForm({ clients, onSaved, existing, knownNames = [] }) {
  const { authHeader } = usePortalAuth();
  const editing = !!existing;
  const [clientId, setClientId] = useState(existing?.client_id || "");
  const [locationId, setLocationId] = useState(existing?.location_id || "");
  const [month, setMonth] = useState(existing?.month || NOW.getMonth() + 1);
  const [year, setYear] = useState(existing?.year || NOW.getFullYear());
  const [visitDate, setVisitDate] = useState(existing?.visit_date || "");
  const walkerOptions = useMemo(() => {
    const merged = [...DEFAULT_WALKERS];
    knownNames.forEach((n) => {
      if (n && !merged.includes(n)) merged.push(n);
    });
    return merged;
  }, [knownNames]);
  const initialConducted = existing?.conducted_by || "";
  const [conductedBy, setConductedBy] = useState(initialConducted);
  const [customName, setCustomName] = useState("");
  const [customMode, setCustomMode] = useState(false);
  const [scores, setScores] = useState(() => (existing ? scoresFromExisting(existing) : emptyScores()));
  const [commentary, setCommentary] = useState(existing?.category_commentary || {});
  const [opportunities, setOpportunities] = useState(() => oppsFromExisting(existing));
  const [summary, setSummary] = useState(existing?.executive_summary || "");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState("");

  const locations = useMemo(() => {
    const c = clients.find((x) => x.id === clientId);
    return c?.locations || [];
  }, [clients, clientId]);

  const scoredCount = Object.values(scores).filter((s) => s.score >= 1).length;
  const total = Object.values(scores).reduce((a, s) => a + (s.score || 0), 0);

  const onScore = (id, n) => setScores((p) => ({ ...p, [id]: { ...p[id], score: n } }));
  const onNote = (id, v) => setScores((p) => ({ ...p, [id]: { ...p[id], note: v } }));
  const setOpp = (i, field, v) =>
    setOpportunities((p) => p.map((o, idx) => (idx === i ? { ...o, [field]: v } : o)));

  const submit = async (status) => {
    setError("");
    if (!clientId || !locationId) {
      setError("Select a client and a location.");
      return;
    }
    if (status === "published" && scoredCount < 20) {
      setError(`All 20 points must be scored before publishing. ${scoredCount} of 20 scored.`);
      return;
    }
    const scoreList = Object.entries(scores)
      .filter(([, v]) => v.score >= 1)
      .map(([pid, v]) => ({ point_id: Number(pid), score: v.score, note: v.note || "" }));
    const opps = opportunities
      .filter((o) => o.title.trim())
      .map((o) => ({ title: o.title.trim(), category: o.category, description: o.description, action: o.action }));
    const effectiveConductedBy = customMode ? customName.trim() : conductedBy;
    const payload = {
      client_id: clientId,
      location_id: locationId,
      month,
      year,
      visit_date: visitDate || null,
      conducted_by: effectiveConductedBy,
      scores: scoreList,
      category_commentary: commentary,
      opportunities: opps,
      executive_summary: summary,
      status,
    };
    setSaving(status);
    try {
      if (editing) {
        const { client_id, location_id, ...patch } = payload;
        await axios.patch(`${OW_API}/admin/ow/reports/${existing.id}`, patch, { headers: authHeader });
      } else {
        await axios.post(`${OW_API}/admin/ow/reports`, payload, { headers: authHeader });
        // reset only for new reports
        setScores(emptyScores());
        setCommentary({});
        setOpportunities([
          { title: "", category: "", description: "", action: "" },
          { title: "", category: "", description: "", action: "" },
          { title: "", category: "", description: "", action: "" },
        ]);
        setSummary("");
        setVisitDate("");
        setConductedBy("");
        setCustomName("");
        setCustomMode(false);
      }
      onSaved?.(status);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving("");
    }
  };

  const inputCls = "w-full border border-brand-200 px-3 py-2.5 text-sm focus:outline-none focus:border-brand-500";

  return (
    <div className="space-y-6" data-testid="report-form">
      {/* meta */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-[0.18em] text-ink-muted">Client</label>
          <select
            value={clientId}
            onChange={(e) => {
              setClientId(e.target.value);
              setLocationId("");
            }}
            className={`mt-1.5 ${inputCls}`}
            data-testid="report-client-select"
            disabled={editing}
          >
            <option value="">Select a client</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.company_name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.18em] text-ink-muted">Location</label>
          <select
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            className={`mt-1.5 ${inputCls}`}
            data-testid="report-location-select"
            disabled={editing || !clientId}
          >
            <option value="">Select a location</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-3 gap-3 md:col-span-2">
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-ink-muted">Month</label>
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className={`mt-1.5 ${inputCls}`} data-testid="report-month">
              {MONTHS.map((m, i) => (
                <option key={m} value={i + 1}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-ink-muted">Year</label>
            <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className={`mt-1.5 ${inputCls}`} data-testid="report-year" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-ink-muted">Visit date</label>
            <input type="date" value={visitDate} onChange={(e) => setVisitDate(e.target.value)} className={`mt-1.5 ${inputCls}`} data-testid="report-visit-date" />
          </div>
        </div>
        <div className="md:col-span-2">
          <label className="text-xs uppercase tracking-[0.18em] text-ink-muted">Conducted by</label>
          <div className="mt-1.5 grid grid-cols-1 md:grid-cols-2 gap-3">
            <select
              value={customMode ? CUSTOM_OPTION : conductedBy}
              onChange={(e) => {
                if (e.target.value === CUSTOM_OPTION) {
                  setCustomMode(true);
                } else {
                  setCustomMode(false);
                  setConductedBy(e.target.value);
                }
              }}
              className={inputCls}
              data-testid="report-conducted-by-select"
            >
              <option value="">Select who performed the walk</option>
              {walkerOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
              {initialConducted && !walkerOptions.includes(initialConducted) && (
                <option value={initialConducted}>{initialConducted}</option>
              )}
              <option value={CUSTOM_OPTION}>Add another name...</option>
            </select>
            {customMode && (
              <input
                type="text"
                placeholder="Full name"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                maxLength={120}
                className={inputCls}
                data-testid="report-conducted-by-custom"
              />
            )}
          </div>
        </div>
      </div>

      {/* scoring */}
      <div className="bg-brand-100 border border-brand-200 px-4 py-3 flex items-center justify-between">
        <span className="text-sm text-ink-soft">
          {scoredCount} of 20 points scored
        </span>
        <span className="font-serif text-2xl text-brand-700">{total}/100</span>
      </div>

      {RUBRIC.map((cat) => (
        <div key={cat.key} className="border border-brand-200" data-testid={`report-category-${cat.key}`}>
          <div className="bg-brand-100 px-5 py-3 font-serif text-xl text-brand-700">{cat.title}</div>
          <div className="px-5">
            {cat.points.map((p) => (
              <ScoreRow key={p.id} point={p} value={scores[p.id]} onScore={onScore} onNote={onNote} />
            ))}
          </div>
          <div className="px-5 pb-5">
            <textarea
              placeholder={`Category commentary for ${cat.title} (optional)`}
              value={commentary[cat.key] || ""}
              onChange={(e) => setCommentary((p) => ({ ...p, [cat.key]: e.target.value }))}
              className="w-full border border-brand-100 px-3 py-2 text-sm focus:outline-none focus:border-brand-400"
              rows={2}
              data-testid={`commentary-${cat.key}`}
            />
          </div>
        </div>
      ))}

      {/* opportunities */}
      <div>
        <div className="font-serif text-xl text-brand-700 mb-3">Top 3 Opportunities</div>
        <div className="space-y-4">
          {opportunities.map((o, i) => (
            <div key={i} className="border border-brand-200 p-4 space-y-3" data-testid={`opportunity-${i}`}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input
                  type="text"
                  placeholder={`Opportunity ${i + 1} title`}
                  value={o.title}
                  onChange={(e) => setOpp(i, "title", e.target.value)}
                  className={`md:col-span-2 ${inputCls}`}
                  data-testid={`opp-title-${i}`}
                />
                <select value={o.category} onChange={(e) => setOpp(i, "category", e.target.value)} className={inputCls} data-testid={`opp-category-${i}`}>
                  <option value="">Category</option>
                  {RUBRIC.map((c) => (
                    <option key={c.key} value={c.key}>
                      {CATEGORY_TITLE[c.key]}
                    </option>
                  ))}
                </select>
              </div>
              <textarea
                placeholder="Description"
                value={o.description}
                onChange={(e) => setOpp(i, "description", e.target.value)}
                className={inputCls}
                rows={2}
                data-testid={`opp-description-${i}`}
              />
              <textarea
                placeholder="Recommended action"
                value={o.action}
                onChange={(e) => setOpp(i, "action", e.target.value)}
                className={inputCls}
                rows={2}
                data-testid={`opp-action-${i}`}
              />
            </div>
          ))}
        </div>
      </div>

      {/* exec summary */}
      <div>
        <label className="text-xs uppercase tracking-[0.18em] text-ink-muted">Executive summary (optional)</label>
        <textarea
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          className={`mt-1.5 ${inputCls}`}
          rows={4}
          data-testid="report-summary"
        />
      </div>

      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2" data-testid="report-error">
          {error}
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <button
          onClick={() => submit("draft")}
          disabled={!!saving}
          className="inline-flex items-center gap-2 border border-brand-300 text-brand-700 hover:bg-brand-100 px-6 py-3 text-sm font-medium transition-colors disabled:opacity-60"
          data-testid="report-save-draft"
        >
          {saving === "draft" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save as draft
        </button>
        <button
          onClick={() => submit("published")}
          disabled={!!saving}
          className="inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-6 py-3 text-sm font-medium transition-colors disabled:opacity-60"
          data-testid="report-publish"
        >
          {saving === "published" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Publish to client
        </button>
      </div>
    </div>
  );
}
